import { useState } from 'react';
import { Users, Pencil, Trash2, AlertCircle, Plus } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useUsers, type UserListItem } from '@/hooks/useUsers';
import { getInitials, getAvatarColor } from '@/lib/avatar';
import { AddUserModal } from '@/components/AddUserModal';
import { EditUserModal } from '@/components/EditUserModal';
import { DeleteUserDialog } from '@/components/DeleteUserDialog';

/**
 * UserManagementCard Component
 *
 * Displays user management controls for admin users only.
 * Matches the approved UI design in docs/views/settings.html lines 260-364.
 *
 * Tasks: M4.5.2.1, M4.5.2.2, M4.5.2.3, M4.5.2.4, M4.5.2.5, M4.5.2.6, M4.5.2.7, M4.5.3, M4.5.4
 */
export function UserManagementCard() {
  const currentUser = useAuthStore(state => state.user);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserListItem | null>(null);

  // Admin-only visibility check (M4.5.2.2)
  if (currentUser?.role !== 'admin' && currentUser?.role !== 'super_admin') {
    return null;
  }

  const handleEditUser = (user: UserListItem) => {
    setEditingUser(user);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
  };

  const handleDeleteUser = (user: UserListItem) => {
    setDeletingUser(user);
  };

  const handleCloseDeleteDialog = () => {
    setDeletingUser(null);
  };

  return (
    <>
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Card Header */}
        <div className="p-4 sm:p-5 border-b border-border">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">User Management</h2>
                <p className="text-sm text-muted-foreground">Manage team members</p>
              </div>
            </div>
            {/* Admin Only Badge (M4.5.2.2) */}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 shrink-0">
              Admin Only
            </span>
          </div>
        </div>

        {/* Card Body - User List */}
        <div className="p-4 sm:p-5">
          <UserList
            currentUserId={currentUser.id}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
          />

          {/* Add New User Button (M4.5.3) */}
          <button
            onClick={() => setIsAddUserModalOpen(true)}
            className="w-full px-4 py-2.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New User
          </button>
        </div>
      </div>

      {/* Add User Modal (M4.5.3) */}
      <AddUserModal open={isAddUserModalOpen} onClose={() => setIsAddUserModalOpen(false)} />

      {/* Edit User Modal (M4.5.4) */}
      <EditUserModal
        open={editingUser !== null}
        onClose={handleCloseEditModal}
        user={editingUser}
      />

      {/* Delete User Dialog (M4.5.5) */}
      <DeleteUserDialog
        open={deletingUser !== null}
        onClose={handleCloseDeleteDialog}
        user={deletingUser}
      />
    </>
  );
}

/**
 * UserList Component - Handles fetching and displaying users (M4.5.2.3)
 */
function UserList({
  currentUserId,
  onEditUser,
  onDeleteUser,
}: {
  currentUserId: string;
  onEditUser: (user: UserListItem) => void;
  onDeleteUser: (user: UserListItem) => void;
}) {
  const { data, isLoading, isError, error } = useUsers({ pageSize: 50 });

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3 mb-4">
        {[1, 2, 3].map(i => (
          <UserListItemSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-red-800">Failed to load users</p>
          <p className="text-xs text-red-600 truncate">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  const users = data?.data ?? [];

  // Empty state
  if (users.length === 0) {
    return (
      <div className="mb-4 p-4 rounded-lg bg-muted/50 text-center">
        <p className="text-sm text-muted-foreground">No users found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 mb-4">
      {users.map(user => (
        <UserListItemRow
          key={user.id}
          user={user}
          isCurrentUser={user.id === currentUserId}
          onEdit={() => onEditUser(user)}
          onDelete={() => onDeleteUser(user)}
        />
      ))}
    </div>
  );
}

/**
 * Loading skeleton for user list items
 */
function UserListItemSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 animate-pulse">
      <div className="w-9 h-9 rounded-full bg-muted shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 bg-muted rounded" />
        <div className="h-3 w-48 bg-muted rounded" />
      </div>
    </div>
  );
}

/**
 * Individual user list item row (M4.5.2.4, M4.5.2.5, M4.5.2.6, M4.5.2.7, M4.5.4)
 */
function UserListItemRow({
  user,
  isCurrentUser,
  onEdit,
  onDelete,
}: {
  user: UserListItem;
  isCurrentUser: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg bg-muted/50 group">
      {/* Avatar and User Info */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Avatar with initials (M4.5.2.4) */}
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${getAvatarColor(user.name)}`}
        >
          <span className="text-xs font-medium">{getInitials(user.name)}</span>
        </div>
        {/* Name and email */}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm text-foreground truncate">{user.name}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        </div>
      </div>

      {/* Right side: Role, Status, (You), Actions */}
      <div className="flex items-center justify-between sm:justify-end gap-2 pl-12 sm:pl-0">
        {/* Role badge and status indicators */}
        <div className="flex items-center gap-2">
          {/* Role Badge (M4.5.2.5) */}
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              user.role === 'admin'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            {user.role === 'admin' ? 'Admin' : 'Standard'}
          </span>

          {/* Status indicator */}
          <span
            className={`text-xs ${user.status === 'active' ? 'text-green-600' : 'text-amber-600'}`}
          >
            {user.status === 'active' ? 'Active' : 'Invited'}
          </span>

          {/* (You) indicator (M4.5.2.6) */}
          {isCurrentUser && <span className="text-xs text-primary font-medium">(You)</span>}
        </div>

        {/* Edit/Delete buttons - hover reveal on desktop, always visible on mobile (M4.5.2.7) */}
        {!isCurrentUser && (
          <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            {/* Edit button (M4.5.4) */}
            <button
              onClick={onEdit}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-colors"
              title="Edit user"
            >
              <Pencil className="w-4 h-4" />
            </button>
            {/* Delete button (M4.5.5) */}
            <button
              onClick={onDelete}
              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
              title="Delete user"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
